import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    MOTIF_TYPE_OPTIONS,
    VIEW_DIRECTION_OPTIONS,
    photoInputSchema,
    type PhotoDocType,
    type FindingDocType,
    type FindingPlanumDocType,
    type FindingProfileDocType
} from '@ast-data/shared';
import {
    joinedListSortComparator,
    dateColumn,
    type GridColDef,
    naturalSortComparator
} from '../../lib/dataGrid';
import { useRecordActionsColumn } from '../../components/useGridRowActions';
import { useAuditColumns } from '../../components/auditColumns';
import { HIDDEN_AUDIT_COLUMNS } from '../../components/auditFields';
import {
    useInlineEditColumn,
    type InlineField
} from '../../components/gridInlineEdit';
import { withRecentValues } from '../../components/recentValues';
import { usePersonUserId } from '../users/personUserId';
import { useIsoWeekColumn } from '../../components/isoWeekColumn';
import {
    useDetailColumnHeader,
    useFindingRefFilterColumns
} from './findingRefLabels';
import { useFindingRefsColumn } from './useFindingRefsColumn';

/**
 * The rest of the photo record — offered in the column picker and the filter
 * panel, hidden by default. `photographerId` (the app-user link behind the
 * name) and the reference id arrays stay out; the audit stamps come from
 * {@link useAuditColumns}, hidden the same way.
 */
const PHOTO_DETAIL_FIELDS = [
    'area',
    'trench',
    'planum',
    'profile',
    'motif',
    'filmNumber',
    'imageNumber'
] as const;

/**
 * The Foto fields editable in the grid — one definition for the columns and the
 * save, like the Funde (see useInlineFindFields).
 *
 * Motiv, Film- und Bildnummer are the desk work: a camera roll is gone through
 * afterwards and written down row by row. The Fotograf carries its account link
 * along, resolved by the same rule the form saves by.
 */
export function useInlinePhotoFields(
    photos: readonly PhotoDocType[]
): InlineField[] {
    const { resolve } = usePersonUserId();

    return useMemo(
        () =>
            /**
             * Every field but the Bildnummer offers its last values on F2: a
             * film is shot in one go, its numbering counts up (see
             * withRecentValues).
             */
            withRecentValues(
                [
                    { field: 'motifType', editor: MOTIF_TYPE_EDITOR },
                    { field: 'viewDirection', editor: VIEW_DIRECTION_EDITOR },
                    { field: 'motif' },
                    { field: 'filmNumber' },
                    { field: 'imageNumber' },
                    {
                        field: 'photographer',
                        editor: { kind: 'person' },
                        derive: (value) => ({
                            photographerId: resolve(String(value ?? ''))
                        })
                    }
                ],
                photos,
                ['imageNumber']
            ),
        [resolve, photos]
    );
}

const MOTIF_TYPE_EDITOR = {
    kind: 'suggest',
    options: MOTIF_TYPE_OPTIONS
} as const;
const VIEW_DIRECTION_EDITOR = {
    kind: 'suggest',
    options: VIEW_DIRECTION_OPTIONS
} as const;

/** Detail columns shown anyway — the ones edited here (see the Funde grid). */
const VISIBLE_PHOTO_DETAIL_FIELDS: readonly string[] = ['motif'];

/** `columnVisibilityModel` entries hiding the detail columns by default. */
export const HIDDEN_PHOTO_DETAIL_COLUMNS: Record<string, boolean> = {
    ...Object.fromEntries(
        PHOTO_DETAIL_FIELDS.filter(
            (field) => !VISIBLE_PHOTO_DETAIL_FIELDS.includes(field)
        ).map((field) => [field, false])
    ),
    ...HIDDEN_AUDIT_COLUMNS,
    publishable: false,
    findSlipNumbers: false
};

/** Column definitions for the Fotos grid. */
export function usePhotosColumns({
    findings,
    planums,
    profiles,
    canEdit,
    inlineFields = [],
    onEdit,
    onDelete,
    onHistory
}: {
    findings: FindingDocType[];
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    canEdit: boolean;
    /** Which columns are edited in the grid — see useInlinePhotoFields. */
    inlineFields?: readonly InlineField[];
    onEdit: (row: PhotoDocType) => void;
    onDelete: (row: PhotoDocType) => void;
    onHistory: (row: PhotoDocType) => void;
}): GridColDef<PhotoDocType>[] {
    const intl = useIntl();
    const detailHeader = useDetailColumnHeader('photo');
    const actionsColumn = useRecordActionsColumn<PhotoDocType>({
        canEdit,
        onEdit,
        onDelete,
        onHistory
    });

    const auditColumns = useAuditColumns<PhotoDocType>();
    const inlineEdit = useInlineEditColumn<PhotoDocType>(
        photoInputSchema,
        canEdit
    );
    const inlineOf = (field: string) =>
        inlineFields.find((spec) => spec.field === field);
    const isoWeekColumn = useIsoWeekColumn<PhotoDocType>(
        (row) => row.recordedAt
    );

    const refsColumn = useFindingRefsColumn<PhotoDocType>({
        headerId: 'photo.field.findings',
        findings,
        planums,
        profiles
    });
    // Hidden, filterable Befundnummer/Fläche/Planum/Profil columns for the filter panel.
    const findingFilterColumns = useFindingRefFilterColumns<PhotoDocType>(
        findings,
        planums,
        profiles
    );

    return [
        {
            field: 'fileName',
            headerName: intl.formatMessage({ id: 'photo.field.fileName' }),
            flex: 1,
            minWidth: 180,
            // Numeric-aware so "..._2.jpg" sorts before "..._10.jpg".
            sortComparator: naturalSortComparator,
            // Bold like the other entities' key column — it is the row's key.
            cellClassName: 'grid-cell-bold'
        },
        // Straight after the pinned key column: when a record was taken is the
        // first thing anyone reads off a row (same order in every grid).
        isoWeekColumn,
        dateColumn<PhotoDocType>({
            field: 'recordedAt',
            headerName: intl.formatMessage({ id: 'common.recordedAt' }),
            width: 120
        }),
        {
            field: 'motifType',
            ...(inlineOf('motifType')
                ? inlineEdit('motifType', inlineOf('motifType'))
                : {}),
            headerName: intl.formatMessage({ id: 'photo.field.motifType' }),
            width: 150
        },
        {
            field: 'remarks',
            type: 'longText',
            headerName: intl.formatMessage({ id: 'photo.field.remarks' }),
            width: 150
        },
        { ...refsColumn, width: 240 },
        {
            field: 'viewDirection',
            ...(inlineOf('viewDirection')
                ? inlineEdit('viewDirection', inlineOf('viewDirection'))
                : {}),
            headerName: intl.formatMessage({ id: 'photo.field.viewDirection' }),
            width: 120
        },
        {
            field: 'photographer',
            ...(inlineOf('photographer')
                ? inlineEdit('photographer', inlineOf('photographer'))
                : {}),
            headerName: intl.formatMessage({ id: 'photo.field.photographer' }),
            width: 150
        },
        ...findingFilterColumns,
        ...PHOTO_DETAIL_FIELDS.map<GridColDef<PhotoDocType>>((field) => ({
            field,
            headerName: detailHeader(field),
            width: 140,
            ...(inlineOf(field) ? inlineEdit(field, inlineOf(field)) : {})
        })),
        {
            field: 'publishable',
            type: 'boolean',
            headerName: intl.formatMessage({ id: 'photo.field.publishable' }),
            width: 130
        },
        {
            field: 'findSlipNumbers',
            headerName: intl.formatMessage({
                id: 'photo.field.findSlipNumbers'
            }),
            width: 160,
            sortComparator: joinedListSortComparator,
            valueGetter: (_value, row) => row.findSlipNumbers.join(', ')
        },
        ...auditColumns,
        actionsColumn
    ];
}
